import styled, { css } from 'styled-components';
import Input from 'antd/es/input';

import BaseCustomIcon from '@components/utils/CustomIcon';
import { CommonUIProps, GetSCProps } from '@utils/types';

import type { DropdownAppearance } from './DropdownInterface';

export const CustomIcon = styled(BaseCustomIcon)`
  display: flex;
  color: ${({ theme }) => theme.palette.grey800};
`;

export const LabelContainer = styled.div`
  height: 100%;
  width: 100%;
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

export const Separator = styled.div<{
  visible: boolean;
  withAutoComplete?: boolean;
}>`
  background-color: ${({ color, theme }) =>
    color || theme.palette.inputBorderColor};

  ${({ visible, withAutoComplete }) =>
    visible && !withAutoComplete
      ? css`
          height: 1px;
          width: 100%;
          position: absolute;
          left: 0px;
          bottom: 0px;
        `
      : css`
          height: 0px;
        `}
`;

const positionMixin = ({ isOpen = false }) =>
  isOpen
    ? css`
        width: calc(100% + 1px);
        left: -0.5px;
      `
    : css`
        width: calc(100%);
        left: 0;
      `;
export const OptionsContainer = styled.div<{
  isOpen: boolean;
  hasError?: boolean;
  optionsZIndex?: number;
  backgroundColor?: string;
  color?: string;
}>`
  position: absolute;
  top: 100%;
  overflow: hidden;
  z-index: ${({ optionsZIndex = 3 }) => optionsZIndex};
  background-color: ${({ theme }) => theme.palette.white};
  ul,
  li {
    background-color: ${({ theme }) => theme.palette.white};
  }
  border: 1px solid
    ${({ theme, hasError }) =>
      hasError ? theme.palette.errorColor : theme.palette.inputBorderColor};
  border-top: none;
  border-bottom-left-radius: 4px;
  border-bottom-right-radius: 4px;

  ul {
    padding: 0;
    margin: 0;
    max-height: 225px;
    overflow-y: scroll;
    list-style: none;
    position: relative;
  }
  li {
    height: 40px;
    color: ${({ color, theme }) => color || theme.palette.matterHorn};
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 16px 24px;
  }
  ${positionMixin}
`;

type ContainerProps = GetSCProps<
  'isOpen' | 'height',
  {
    $size: 'small' | 'medium' | 'large' | 'xlarge';
    $appearance: DropdownAppearance;
    displayPlaceholder: boolean;
    hasError?: boolean;
    disabled?: boolean;
  } & CommonUIProps
>;

const labelColorMixin = ({
  displayPlaceholder,
  theme,
  customColor,
  $appearance,
}: ContainerProps) =>
  $appearance === 'form'
    ? css`
        color: ${customColor ||
        (displayPlaceholder
          ? theme?.palette.inputPlaceholderColor
          : theme?.palette.grey1100)};
        opacity: 1;
        font-size: 14px;
        font-weight: 500;
        line-height: 24px;
        word-wrap: break-word;
      `
    : displayPlaceholder
    ? css`
        color: ${customColor || theme?.palette.grey1100};
        opacity: 0.8;
      `
    : css`
        color: ${customColor || theme?.palette.grey1100};
        font-size: 14px;
        line-height: 24px;
        word-wrap: break-word;
      `;
const PADDINGS = {
  small: css`
    padding: 8px 12px;
    font-size: 12px;
    line-height: 16px;
  `,
  medium: css`
    padding: 6px 12px;
    font-size: 14px;
    line-height: 24px;
  `,
  large: css`
    padding: 8px 12px;
    font-size: 14px;
    line-height: 24px;
  `,
  xlarge: css`
    padding: 16px 12px;
    font-size: 14px;
    line-height: 24px;
  `,
};
export const Container = styled.div<ContainerProps>`
  display: flex;
  flex-direction: column;
  justify-content: center;
  width: 100%;
  ${({ $size }) => PADDINGS[$size]};
  background-color: ${({ customBackgroundColor, theme }) =>
    customBackgroundColor || theme.palette.white};
  position: relative;
  border: 1px solid
    ${({ theme, hasError }) =>
      hasError ? theme.palette.errorColor : theme.palette.inputBorderColor};
  border-radius: 4px;
  cursor: pointer;
  ${(p) =>
    p.isOpen
      ? css`
          border-bottom-left-radius: 0;
          border-bottom-right-radius: 0;
          border-bottom: 1px solid transparent;
          z-index: 9999;
        `
      : null}
  height: ${({ height }) => height ?? 'auto'};

  box-sizing: border-box;
  ${LabelContainer} {
    ${labelColorMixin}
    display: flex;
    align-items: center;
  }
  position: relative;
  opacity: ${(p) => (p.disabled ? 0.5 : 1)};
  pointer-events: ${(p) => (p.disabled ? 'none' : 'all')};
`;

export const Options = styled.ul`
  &::-webkit-scrollbar {
    width: 6px;
  }
  &::-webkit-scrollbar-thumb {
    background-color: ${(p) => p.theme.palette.white};
    border-radius: 2.5px;
    height: 59px;
  }
`;

type OptionProps = GetSCProps<
  'isActive',
  {
    isHighlighted: boolean;
  }
>;

export const Option = styled.li<OptionProps>`
  & > span {
    text-overflow: ellipsis;
    overflow: hidden;
    white-space: nowrap;
  }

  &:hover {
    background-color: ${({ theme }) => theme.palette.grey100};
    cursor: pointer;

    & label,
    & span {
      cursor: pointer;
    }
  }
`;

export const SearchInput = styled(Input)`
  &&& {
    height: 44px;
    border-radius: 0px;

    .setur-input-wrapper {
      height: 100%;
    }

    .setur-input {
      height: 100%;
      border: 1px solid ${(p) => p.theme.palette.inputBorderColor};
      border-right: none;
      &:focus {
        box-shadow: none;
        border: 1px solid ${(p) => p.theme.palette.inputBorderColor};
      }
    }
    .setur-input-group-addon {
      background-color: ${(p) => p.theme.palette.white};
      border: 1px solid ${(p) => p.theme.palette.inputBorderColor};
      border-left: none;
    }
  }
`;
